import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useThemeStore, useAuthStore } from '@/stores';
import { Bell, Send, CheckCircle2, AlertCircle, RefreshCw, Eye, Trash2, Smartphone, ShieldCheck, ShieldAlert, Sparkles, ChevronLeft, ChevronRight } from 'lucide-react';
import { apiClient } from '@/services/api-client';

interface NotificationLog {
  id: string;
  title: string;
  body: string;
  type: 'TICKET' | 'SUBSCRIPTION' | 'DEBT' | 'GENERAL';
  status: 'SENT' | 'READ' | 'UNREAD';
  createdAt: number;
  success?: boolean;
  error?: string;
}

const NotificationCenterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useThemeStore();
  const { user } = useAuthStore();
  const isLight = theme === 'light';

  const [logs, setLogs] = useState<NotificationLog[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = 10;
  const [stats, setStats] = useState({
    sentCount: 0,
    deviceCount: 0,
    successCount: 0,
    failureCount: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>('ALL');

  const [permission, setPermission] = useState<string>(window.Notification?.permission || 'default');
  const [subscriptionId, setSubscriptionId] = useState<string | null>(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [testErrorMessage, setTestErrorMessage] = useState('');
  const [isRequestingPermission, setIsRequestingPermission] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [consoleLogs, setConsoleLogs] = useState<string[]>([]);
  const [debugCollapsed, setDebugCollapsed] = useState(true);

  // Capture console logs into state for on-screen mobile debugging
  useEffect(() => {
    const handleLog = (type: string, args: any[]) => {
      const msg = `[${type} ${new Date().toLocaleTimeString()}] ${args.map(a => typeof a === 'object' ? JSON.stringify(a) : String(a)).join(' ')}`;
      setConsoleLogs(prev => [...prev.slice(-39), msg]);
    };

    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;

    console.log = (...args) => {
      originalLog.apply(console, args);
      handleLog('INFO', args);
    };
    console.warn = (...args) => {
      originalWarn.apply(console, args);
      handleLog('WARN', args);
    };
    console.error = (...args) => {
      originalError.apply(console, args);
      handleLog('ERROR', args);
    };

    return () => {
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    // Detect standalone PWA mode
    const standaloneActive = 
      ('standalone' in window.navigator && (window.navigator as any).standalone) || 
      window.matchMedia('(display-mode: standalone)').matches;
    setIsStandalone(standaloneActive);

    const cleanSubId = (id: any): string | null => {
      if (typeof id === 'string' && id.trim().length > 5 && id !== 'false' && id !== 'null' && id !== 'undefined') {
        return id.trim();
      }
      return null;
    };

    // Retrieve subscription ID from OneSignal initially
    const OneSignal = (window as any).OneSignal;
    if (OneSignal) {
      const rawSubId = OneSignal.User?.pushSubscription?.id || OneSignal.User?.PushSubscription?.id;
      setSubscriptionId(cleanSubId(rawSubId));
      setPermission(window.Notification?.permission || 'default');
    }

    const OneSignalDeferred = (window as any).OneSignalDeferred || [];
    OneSignalDeferred.push((os: any) => {
      const rawSubId = os.User?.pushSubscription?.id || os.User?.PushSubscription?.id;
      setSubscriptionId(cleanSubId(rawSubId));
      setPermission(window.Notification?.permission || 'default');

      // Listen for permission change event
      os.Notifications?.addEventListener('permissionChange', (perm: any) => {
        setPermission(window.Notification?.permission || 'default');
        const updatedSubId = os.User?.pushSubscription?.id || os.User?.PushSubscription?.id;
        setSubscriptionId(cleanSubId(updatedSubId));
      });

      // Listen for push subscription change event (vital when subscription is created/loaded asynchronously)
      const sub = os.User?.pushSubscription || os.User?.PushSubscription;
      if (sub) {
        sub.addEventListener('change', (event: any) => {
          console.log('[NotificationCenter] pushSubscription changed event:', event);
          const currentId = event?.current?.id || os.User?.pushSubscription?.id || os.User?.PushSubscription?.id;
          setSubscriptionId(cleanSubId(currentId));
        });
      }
    });

    // Fallback periodic checker to ensure subscription ID is loaded once OneSignal finishes syncing
    let attempts = 0;
    const interval = setInterval(() => {
      const os = (window as any).OneSignal;
      const rawSubId = os?.User?.pushSubscription?.id || os?.User?.PushSubscription?.id;
      const validated = cleanSubId(rawSubId);
      if (validated) {
        setSubscriptionId(validated);
        setPermission(window.Notification?.permission || 'default');
        clearInterval(interval);
      }
      attempts++;
      if (attempts > 15) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Effect to automatically register device with backend when subscriptionId is detected/changes
  useEffect(() => {
    if (subscriptionId && subscriptionId !== 'false' && subscriptionId !== 'null' && subscriptionId !== 'undefined' && user?.username) {
      apiClient.post('/notifications/register-device', { subscriptionId })
        .then(() => {
          console.log('[NotificationCenter] Auto-registered device:', subscriptionId);
          fetchStatsAndLogs(); // Refresh stats on UI to increment device count
        })
        .catch((err) => {
          console.warn('[NotificationCenter] Auto-registration failed:', err);
        });
    }
  }, [subscriptionId, user]);

  const requestPermission = async () => {
    setIsRequestingPermission(true);
    setRequestError(null);
    try {
      if (typeof window !== 'undefined' && 'Notification' in window) {
        const perm = await Notification.requestPermission();
        setPermission(perm);
      }

      const OneSignal = (window as any).OneSignal;
      let subId: string | null = null;

      if (OneSignal) {
        try {
          const sub = OneSignal.User?.pushSubscription || OneSignal.User?.PushSubscription;
          if (sub) {
            await Promise.all([
              sub.optIn().catch(() => {}),
              OneSignal.Notifications?.requestPermission ? OneSignal.Notifications.requestPermission().catch(() => {}) : Promise.resolve(),
              user?.username ? OneSignal.login(user.username).catch(() => {}) : Promise.resolve()
            ]);
            subId = OneSignal.User?.pushSubscription?.id || OneSignal.User?.PushSubscription?.id;
          }
        } catch (osErr) {
          console.warn('[NotificationCenter] OneSignal setup notice:', osErr);
        }
      }

      const activeSubId = subId || subscriptionId || 'device-registered-' + Date.now();
      setSubscriptionId(activeSubId);
      await apiClient.post('/notifications/register-device', { subscriptionId: activeSubId });
      fetchStatsAndLogs();
    } catch (err: any) {
      console.error('Failed to request permission:', err);
      setRequestError(err.message || String(err));
    } finally {
      setIsRequestingPermission(false);
    }
  };

  const sendTestNotification = async () => {
    setTestStatus('sending');
    setTestErrorMessage('');
    try {
      const targetUser = user?.username || 'anonymous';
      const OneSignal = (window as any).OneSignal;
      const currentSubId = OneSignal?.User?.pushSubscription?.id || OneSignal?.User?.PushSubscription?.id || subscriptionId || 'device-local-123';

      const title = '🔔 إشعار تجريبي ناجح!';
      const message = `مرحباً ${user?.firstName || user?.username || ''}! تم استقبال هذا الإشعار بنجاح في الخلفية. 🎉`;

      // 1. Request/Ensure browser notification permission if not yet granted
      if (typeof window !== 'undefined' && 'Notification' in window) {
        let currentPerm = Notification.permission;
        if (currentPerm === 'default') {
          currentPerm = await Notification.requestPermission();
          setPermission(currentPerm);
        }

        // 2. Trigger native OS / browser notification popup
        if (currentPerm === 'granted') {
          let notificationFired = false;

          if ('serviceWorker' in navigator) {
            try {
              const reg = await navigator.serviceWorker.ready;
              if (reg && reg.showNotification) {
                await reg.showNotification(title, {
                  body: message,
                  icon: '/logo.png',
                  badge: '/logo.png',
                  dir: 'rtl',
                  vibrate: [200, 100, 200],
                  data: { url: window.location.href }
                } as any);
                notificationFired = true;
              }
            } catch (swErr) {
              console.warn('[NotificationCenter] ServiceWorker showNotification notice:', swErr);
            }
          }

          if (!notificationFired) {
            try {
              new Notification(title, {
                body: message,
                icon: '/logo.png',
                dir: 'rtl'
              });
            } catch (nErr) {
              console.warn('[NotificationCenter] Native Notification constructor notice:', nErr);
            }
          }
        }
      }

      // 3. Post to backend
      const response = await apiClient.post('/notifications/trigger', {
        title,
        message,
        targetUsers: [targetUser],
        targetSubId: currentSubId,
        type: 'GENERAL'
      });

      if (response.data && response.data.success) {
        setTestStatus('success');
        fetchStatsAndLogs();
      } else {
        setTestStatus('error');
        setTestErrorMessage(response.data?.error || 'فشل إرسال الإشعار من السيرفر.');
      }
    } catch (err: any) {
      setTestStatus('error');
      setTestErrorMessage(err.message || 'حدث خطأ غير متوقع أثناء الاتصال بالخادم.');
    }
  };

  const [cronFeedback, setCronFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [refreshFeedback, setRefreshFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const fetchStatsAndLogs = async (isManual = false, targetType?: string) => {
    setIsLoading(true);
    if (isManual) setRefreshFeedback(null);
    try {
      const activeType = targetType !== undefined ? targetType : filterType;
      const queryParams: Record<string, any> = { _t: Date.now() };
      if (activeType && activeType !== 'ALL') {
        queryParams.type = activeType;
      }

      // Fetch stats & logs in parallel with cache-buster timestamp for manual clicks
      const [statsRes, logsRes] = await Promise.all([
        apiClient.get<{ sentCount: number; deviceCount: number; successCount: number; failureCount: number }>('/notifications/admin-stats', { _t: Date.now() }),
        apiClient.get<NotificationLog[]>('/notifications/user-logs', queryParams),
      ]);
      if (statsRes?.data) {
        setStats(statsRes.data);
      }
      if (logsRes?.data && Array.isArray(logsRes.data)) {
        setLogs(logsRes.data);
      }
      if (isManual) {
        setRefreshFeedback({ type: 'success', message: 'تم تحديث بيانات وسجلات مركز التنبيهات بنجاح!' });
        setTimeout(() => setRefreshFeedback(null), 4000);
      }
    } catch (err: any) {
      console.error('Failed to load notification stats/logs:', err);
      if (isManual) {
        const errMsg = err?.response?.data?.message || err?.message || 'تعذر جلب أحدث بيانات التنبيهات من الخادم.';
        setRefreshFeedback({ type: 'error', message: errMsg });
        setTimeout(() => setRefreshFeedback(null), 4000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setPageNumber(1);
    fetchStatsAndLogs(false, filterType);
  }, [filterType]);

  const [isCronRunning, setIsCronRunning] = useState(false);
  const runManualCronCheck = async () => {
    if (isCronRunning) return;
    setIsCronRunning(true);
    setCronFeedback(null);
    try {
      const res = await apiClient.get('/notifications/run-cron-now', { _t: Date.now() });
      const resData = res?.data || res;
      
      // Immediately refresh stats and logs
      await fetchStatsAndLogs(false);

      const successMsg = resData?.message || (resData?.success ? 'تم فحص التنبيهات واكتشاف أحدث الاشتراكات والتذاكر بنجاح!' : 'تم فحص التنبيهات.');
      setCronFeedback({ type: 'success', message: successMsg });
      setTimeout(() => setCronFeedback(null), 6000);
    } catch (err: any) {
      console.error('Failed to trigger cron check:', err);
      const errMsg = err?.response?.data?.message || err?.message || 'حدث خطأ أثناء فحص التنبيهات من الخادم.';
      setCronFeedback({ type: 'error', message: errMsg });
      setTimeout(() => setCronFeedback(null), 6000);
    } finally {
      setIsCronRunning(false);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiClient.post('/notifications/mark-read', { all: true });
      setLogs((prev) => prev.map((l) => ({ ...l, status: 'READ' })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      await apiClient.post('/notifications/mark-read', { notificationId: id });
      setLogs((prev) =>
        prev.map((l) => (l.id === id ? { ...l, status: 'READ' } : l))
      );
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await apiClient.post('/notifications/mark-read', { notificationId: id, delete: true });
      setLogs((prev) => prev.filter((l) => l.id !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const filteredLogs = logs.filter(
    (log) => filterType === 'ALL' || log.type === filterType
  );

  const totalLogsCount = filteredLogs.length;
  const totalPages = Math.max(Math.ceil(totalLogsCount / pageSize), 1);
  const paginatedLogs = filteredLogs.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);

  return (
    <div style={{ padding: '24px', direction: 'rtl', minHeight: '100%', boxSizing: 'border-box' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '24px', fontWeight: 700, margin: '0 0 4px 0' }}>
            {i18n.language === 'ar' ? 'مركز التنبيهات' : 'Notification Center'}
          </h1>
          <p style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '14px', margin: 0 }}>
            إدارة ومراقبة إشعارات الخلفية والتنبيهات المستلمة والتقارير الإحصائية.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={runManualCronCheck}
            disabled={isCronRunning}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: 'linear-gradient(90deg, #2563eb, #3b82f6)',
              border: 'none',
              borderRadius: '10px',
              padding: '10px 18px',
              color: '#ffffff',
              fontSize: '14px',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(37,99,235,0.3)'
            }}
          >
            <Sparkles size={16} className={isCronRunning ? 'animate-spin' : ''} />
            فحص التنبيهات الآن
          </button>
          <button
            onClick={() => fetchStatsAndLogs(true)}
            disabled={isLoading}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.08)',
              border: isLight ? '1px solid #cbd5e1' : '1px solid rgba(255,255,255,0.15)',
              borderRadius: '10px',
              padding: '10px 18px',
              color: isLight ? '#1e293b' : '#e2e8f0',
              fontSize: '14px',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
            تحديث البيانات
          </button>
        </div>
      </div>

      {/* Operation Feedback Banners */}
      {(cronFeedback || refreshFeedback) && (
        <div style={{ marginBottom: '20px', animation: 'fadeIn 0.2s ease-in' }}>
          {cronFeedback && (
            <div
              style={{
                background: cronFeedback.type === 'success' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                border: `1px solid ${cronFeedback.type === 'success' ? 'rgba(16, 185, 129, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
                color: cronFeedback.type === 'success' ? (isLight ? '#047857' : '#34d399') : (isLight ? '#b91c1c' : '#f87171'),
                padding: '12px 16px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                fontWeight: 600,
                marginBottom: refreshFeedback ? '10px' : '0',
              }}
            >
              {cronFeedback.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
              <span>{cronFeedback.message}</span>
            </div>
          )}
          {refreshFeedback && (
            <div
              style={{
                background: refreshFeedback.type === 'success' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                border: `1px solid ${refreshFeedback.type === 'success' ? 'rgba(59, 130, 246, 0.4)' : 'rgba(239, 68, 68, 0.4)'}`,
                color: refreshFeedback.type === 'success' ? (isLight ? '#1d4ed8' : '#60a5fa') : (isLight ? '#b91c1c' : '#f87171'),
                padding: '12px 16px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                fontSize: '14px',
                fontWeight: 600,
              }}
            >
              {refreshFeedback.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
              <span>{refreshFeedback.message}</span>
            </div>
          )}
        </div>
      )}

      {/* Stats Section */}
      <div className="notification-stats-grid" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
        gap: '20px',
        marginBottom: '24px',
      }}>
        {/* Device Count Card */}
        <div style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{ background: 'rgba(59,130,246,0.15)', padding: '12px', borderRadius: '12px' }}>
            <Bell size={24} color="#3b82f6" />
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 600 }}>الأجهزة المشتركة</div>
            <div style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '24px', fontWeight: 800, marginTop: '4px' }}>
              {stats.deviceCount}
            </div>
          </div>
        </div>

        {/* Sent Count Card */}
        <div style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{ background: 'rgba(16,185,129,0.15)', padding: '12px', borderRadius: '12px' }}>
            <Send size={24} color="#10b981" />
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 600 }}>إجمالي الإشعارات المرسلة</div>
            <div style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '24px', fontWeight: 800, marginTop: '4px' }}>
              {stats.sentCount}
            </div>
          </div>
        </div>

        {/* Success Count Card */}
        <div style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{ background: 'rgba(16,185,129,0.15)', padding: '12px', borderRadius: '12px' }}>
            <CheckCircle2 size={24} color="#10b981" />
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 600 }}>الإرسال الناجح</div>
            <div style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '24px', fontWeight: 800, marginTop: '4px' }}>
              {stats.successCount}
            </div>
          </div>
        </div>

        {/* Failure Count Card */}
        <div style={{
          background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
          border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
          borderRadius: '16px',
          padding: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          <div style={{ background: 'rgba(239,68,68,0.15)', padding: '12px', borderRadius: '12px' }}>
            <AlertCircle size={24} color="#ef4444" />
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 600 }}>فشل الإرسال أو غير مسجل</div>
            <div style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '24px', fontWeight: 800, marginTop: '4px' }}>
              {stats.failureCount}
            </div>
          </div>
        </div>
      </div>

      {/* Diagnostics / Setup Panel */}
      <div className="notification-diagnostics-grid" style={{
        background: isLight ? '#ffffff' : 'rgba(13,31,60,0.5)',
        border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(59, 130, 246, 0.15)',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
        gap: '24px',
      }}>
        {/* Left side: current status */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <h3 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '16px', fontWeight: 700, margin: '0 0 4px 0', display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'flex-start' }}>
            <Smartphone size={18} color="#3b82f6" />
            حالة اتصال جهازك الحالي (Device Status)
          </h3>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {/* Permission status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ color: '#94a3b8', fontSize: '13.5px' }}>صلاحية الإشعارات بالمتصفح:</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {permission === 'granted' ? (
                  <span style={{ color: '#10b981', fontSize: '13.5px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ShieldCheck size={16} /> نشط (Granted)
                  </span>
                ) : permission === 'denied' ? (
                  <span style={{ color: '#ef4444', fontSize: '13.5px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <ShieldAlert size={16} /> محظور (Denied)
                  </span>
                ) : (
                  <span style={{ color: '#fbbf24', fontSize: '13.5px', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <AlertCircle size={16} /> غير محدد (Default)
                  </span>
                )}
              </div>
            </div>

            {/* Standalone status */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <span style={{ color: '#94a3b8', fontSize: '13.5px' }}>وضع التطبيق (PWA Mode):</span>
              <span style={{ color: isStandalone ? '#10b981' : '#fbbf24', fontSize: '13.5px', fontWeight: 700 }}>
                {isStandalone ? 'تطبيق مثبت (Standalone)' : 'متصفح ويب (Web Browser)'}
              </span>
            </div>

            {/* Subscription ID */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ color: '#94a3b8', fontSize: '13.5px' }}>معرف جهازك الحالي:</span>
                <span style={{ color: subscriptionId ? '#3b82f6' : '#ef4444', fontSize: '12.5px', fontWeight: 600, fontFamily: 'monospace', maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={subscriptionId || ''}>
                  {subscriptionId || 'غير مسجل'}
                </span>
              </div>
              {permission === 'granted' && !subscriptionId && (
                <div style={{ color: '#fbbf24', fontSize: '11px', marginTop: '4px', textAlign: 'right', lineHeight: 1.4 }}>
                  ⚠️ الصلاحية مقبولة بالمتصفح، لكن لم يتم ربط هذا الجهاز بخدمة الإشعارات بعد. يرجى الضغط على زر "ربط وتفعيل جهازك الآن" لتسجيل المعرف وتفعيل الإشعارات بالخلفية.
                </div>
              )}
              {permission === 'denied' && (
                <div style={{ color: '#ef4444', fontSize: '11.5px', marginTop: '6px', textAlign: 'right', lineHeight: 1.4, fontWeight: 600 }}>
                  ⚠️ تم حظر صلاحية الإشعارات في هذا الجهاز. لتفعيلها، يرجى الانتقال إلى: إعدادات الهاتف (Settings) 🡨 الإشعارات (Notifications) 🡨 FTTH Nexus 🡨 تفعيل السماح بالإشعارات.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right side: actions and helper guide */}
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', gap: '16px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <h3 style={{ color: isLight ? '#0f172a' : '#e2e8f0', fontSize: '15px', fontWeight: 700, margin: 0, textAlign: 'right' }}>
              إجراءات التهيئة والاختبار
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '12px', margin: 0, lineHeight: 1.5, textAlign: 'right' }}>
              يرجى منح الصلاحية لتتمكن من تلقي الإشعارات الفورية للتذاكر في الخلفية عند إغلاق التطبيق.
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', justifyContent: 'flex-start' }}>
            {(permission !== 'granted' || !subscriptionId) && (
              <button
                onClick={requestPermission}
                disabled={isRequestingPermission}
                style={{
                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                  border: 'none',
                  borderRadius: '10px',
                  padding: '10px 20px',
                  color: '#ffffff',
                  fontSize: '13px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
                }}
              >
                <Bell size={16} />
                {permission === 'granted' ? 'ربط وتفعيل جهازك الآن' : 'تفعيل الإشعارات الآن'}
              </button>
            )}

            <button
              onClick={sendTestNotification}
              disabled={testStatus === 'sending'}
              style={{
                background: 'rgba(255, 255, 255, 0.06)',
                border: '1px solid rgba(255, 255, 255, 0.12)',
                borderRadius: '10px',
                padding: '10px 20px',
                color: '#e2e8f0',
                fontSize: '13px',
                fontWeight: 700,
                cursor: testStatus === 'sending' ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Sparkles size={16} color="#3b82f6" />
              إرسال إشعار تجريبي لهاتفي
            </button>
          </div>

          {/* Error & Test Status feedback messages */}
          {requestError && (
            <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600, textAlign: 'right', lineHeight: 1.4 }}>
              ❌ خطأ التفعيل: {requestError}
            </div>
          )}
          {testStatus === 'sending' && (
            <div style={{ color: '#fbbf24', fontSize: '12px', marginTop: '6px', textAlign: 'right' }}>جاري إرسال إشعار الاختبار الفوري...</div>
          )}
          {testStatus === 'success' && (
            <div style={{ color: '#10b981', fontSize: '12px', marginTop: '6px', fontWeight: 600, textAlign: 'right' }}>تم إرسال إشعار فحص فوري لجهازك بنجاح! تفحص هاتفك الآن. 🎉</div>
          )}
          {testStatus === 'error' && (
            <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: 600, textAlign: 'right' }}>فشل الفحص: {testErrorMessage}</div>
          )}
        </div>
      </div>


      {/* Main Content & Logs */}
      <div style={{
        background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
        border: isLight ? '1px solid #e2e8f0' : '1px solid rgba(255,255,255,0.08)',
        borderRadius: '16px',
        padding: '24px',
      }}>
        {/* Filters and Actions */}
        <style>{`
          .filters-actions-container {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            gap: 16px;
            direction: rtl;
          }
          .tabs-outer-container {
            width: 100%;
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;
            -webkit-overflow-scrolling: touch;
            direction: rtl;
          }
          .tabs-outer-container::-webkit-scrollbar {
            display: none;
          }
          .tabs-container {
            display: flex;
            gap: 12px;
            flex-wrap: nowrap;
            direction: rtl;
            justify-content: flex-start;
            align-items: center;
            padding: 8px 4px;
            min-width: max-content;
          }
          .mark-read-btn {
            background: transparent;
            border: none;
            color: #3b82f6;
            font-size: 13.5px;
            font-weight: 600;
            cursor: pointer;
            flex-shrink: 0;
            text-align: left;
            transition: color 0.2s;
          }
          .mark-read-btn:hover:not(:disabled) {
            color: #2563eb;
          }
          
          /* ─── Premium Colored Tab Buttons ─── */
          .tab-button {
            border-radius: 12px;
            padding: 10px 18px;
            font-size: 14px;
            font-weight: 700;
            cursor: pointer;
            transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
            flex-shrink: 0;
            outline: none;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            border: 2px solid transparent;
          }
          
          /* Colors for buttons */
          .tab-all {
            border-color: rgba(99, 102, 241, 0.35);
            background: rgba(99, 102, 241, 0.05);
            color: #a5b4fc;
          }
          .tab-all:hover {
            background: rgba(99, 102, 241, 0.1);
            border-color: rgba(99, 102, 241, 0.6);
          }
          .tab-all.active {
            background: linear-gradient(135deg, #6366f1, #4f46e5);
            border-color: #818cf8;
            color: #ffffff;
            box-shadow: 0 4px 14px rgba(99, 102, 241, 0.4);
          }
          
          .tab-ticket {
            border-color: rgba(59, 130, 246, 0.35);
            background: rgba(59, 130, 246, 0.05);
            color: #93c5fd;
          }
          .tab-ticket:hover {
            background: rgba(59, 130, 246, 0.1);
            border-color: rgba(59, 130, 246, 0.6);
          }
          .tab-ticket.active {
            background: linear-gradient(135deg, #3b82f6, #2563eb);
            border-color: #60a5fa;
            color: #ffffff;
            box-shadow: 0 4px 14px rgba(59, 130, 246, 0.4);
          }
          
          .tab-subscription {
            border-color: rgba(16, 185, 129, 0.35);
            background: rgba(16, 185, 129, 0.05);
            color: #6ee7b7;
          }
          .tab-subscription:hover {
            background: rgba(16, 185, 129, 0.1);
            border-color: rgba(16, 185, 129, 0.6);
          }
          .tab-subscription.active {
            background: linear-gradient(135deg, #10b981, #059669);
            border-color: #34d399;
            color: #ffffff;
            box-shadow: 0 4px 14px rgba(16, 185, 129, 0.4);
          }
          
          .tab-debt {
            border-color: rgba(245, 158, 11, 0.35);
            background: rgba(245, 158, 11, 0.05);
            color: #fcd34d;
          }
          .tab-debt:hover {
            background: rgba(245, 158, 11, 0.1);
            border-color: rgba(245, 158, 11, 0.6);
          }
          .tab-debt.active {
            background: linear-gradient(135deg, #f59e0b, #d97706);
            border-color: #fbbf24;
            color: #ffffff;
            box-shadow: 0 4px 14px rgba(245, 158, 11, 0.4);
          }
          
          .tab-general {
            border-color: rgba(148, 163, 184, 0.35);
            background: rgba(148, 163, 184, 0.05);
            color: #cbd5e1;
          }
          .tab-general:hover {
            background: rgba(148, 163, 184, 0.1);
            border-color: rgba(148, 163, 184, 0.6);
          }
          .tab-general.active {
            background: linear-gradient(135deg, #64748b, #475569);
            border-color: #94a3b8;
            color: #ffffff;
            box-shadow: 0 4px 14px rgba(148, 163, 184, 0.4);
          }
          
          /* Light mode styles */
          body.light-mode .tab-all {
            border-color: rgba(99, 102, 241, 0.45);
            background: rgba(99, 102, 241, 0.04);
            color: #4f46e5;
          }
          body.light-mode .tab-all:hover {
            background: rgba(99, 102, 241, 0.08);
          }
          body.light-mode .tab-ticket {
            border-color: rgba(59, 130, 246, 0.45);
            background: rgba(59, 130, 246, 0.04);
            color: #1d4ed8;
          }
          body.light-mode .tab-ticket:hover {
            background: rgba(59, 130, 246, 0.08);
          }
          body.light-mode .tab-subscription {
            border-color: rgba(16, 185, 129, 0.45);
            background: rgba(16, 185, 129, 0.04);
            color: #047857;
          }
          body.light-mode .tab-subscription:hover {
            background: rgba(16, 185, 129, 0.08);
          }
          body.light-mode .tab-debt {
            border-color: rgba(245, 158, 11, 0.5);
            background: rgba(245, 158, 11, 0.04);
            color: #b45309;
          }
          body.light-mode .tab-debt:hover {
            background: rgba(245, 158, 11, 0.08);
          }
          body.light-mode .tab-general {
            border-color: rgba(100, 116, 139, 0.45);
            background: rgba(100, 116, 139, 0.04);
            color: #334155;
          }
          body.light-mode .tab-general:hover {
            background: rgba(100, 116, 139, 0.08);
          }
          
          /* ─── Pagination Buttons Styling ─── */
          .pagination-btn {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
            border-radius: 10px;
            padding: 8px 16px;
            font-size: 13.5px;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
            border: 1.5px solid transparent;
            outline: none;
          }
          
          /* Light mode active buttons */
          body.light-mode .pagination-btn {
            background: #ffffff;
            border-color: #cbd5e1;
            color: #1e293b;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          }
          body.light-mode .pagination-btn:hover:not(:disabled) {
            background: #f8fafc;
            border-color: #2563eb;
            color: #2563eb;
            box-shadow: 0 4px 12px rgba(37, 99, 235, 0.08);
            transform: translateY(-1px);
          }
          body.light-mode .pagination-btn:active:not(:disabled) {
            transform: translateY(0);
          }
          
          /* Dark mode active buttons */
          .pagination-btn {
            background: rgba(255, 255, 255, 0.06);
            border-color: rgba(255, 255, 255, 0.1);
            color: #e2e8f0;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.15);
          }
          .pagination-btn:hover:not(:disabled) {
            background: rgba(37, 99, 235, 0.15);
            border-color: rgba(59, 130, 246, 0.4);
            color: #60a5fa;
            box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
            transform: translateY(-1px);
          }
          .pagination-btn:active:not(:disabled) {
            transform: translateY(0);
          }
          
          /* Disabled states */
          .pagination-btn:disabled {
            cursor: not-allowed !important;
            box-shadow: none !important;
            transform: none !important;
            opacity: 0.4 !important;
          }
          body.light-mode .pagination-btn:disabled {
            background: #f1f5f9 !important;
            border-color: #cbd5e1 !important;
            color: #94a3b8 !important;
          }
          .pagination-btn:disabled {
            background: rgba(255, 255, 255, 0.02) !important;
            border-color: rgba(255, 255, 255, 0.04) !important;
            color: #475569 !important;
          }
          
          @media (max-width: 768px) {
            .filters-actions-container {
              flex-direction: column;
              align-items: stretch;
              gap: 12px;
            }
            .tabs-outer-container {
              width: 100%;
              text-align: center;
              overflow-x: visible;
            }
            .tabs-container {
              justify-content: center;
              flex-wrap: wrap;
              gap: 6px;
              padding: 4px 0;
              min-width: auto;
            }
            .tab-button {
              padding: 6px 12px;
              font-size: 12px;
              border-radius: 8px;
              border-width: 1.5px;
            }
            .mark-read-btn {
              width: 100%;
              text-align: center;
              padding-right: 0;
            }
          }
        `}</style>
        <div className="filters-actions-container">
          <div className="tabs-outer-container">
            <div className="tabs-container">
              {['ALL', 'TICKET', 'SUBSCRIPTION', 'DEBT', 'GENERAL'].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`tab-button ${
                    type === 'ALL' ? 'tab-all' :
                    type === 'TICKET' ? 'tab-ticket' :
                    type === 'SUBSCRIPTION' ? 'tab-subscription' :
                    type === 'DEBT' ? 'tab-debt' : 'tab-general'
                  } ${filterType === type ? 'active' : ''}`}
                >
                  {type === 'ALL' ? 'الكل' :
                   type === 'TICKET' ? 'التذاكر' :
                   type === 'SUBSCRIPTION' ? 'الاشتراكات' :
                   type === 'DEBT' ? 'الديون' : 'عام'}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={markAllAsRead}
            disabled={logs.every((l) => l.status === 'READ')}
            className="mark-read-btn"
            style={{
              opacity: logs.every((l) => l.status === 'READ') ? 0.5 : 1,
            }}
          >
            تحديد الكل كمقروء
          </button>
        </div>

        {/* Logs Table / List */}
        {filteredLogs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>
            <Bell size={48} style={{ margin: '0 auto 16px auto', opacity: 0.3 }} />
            <div>لا توجد إشعارات حالية تطابق التصفية المحددة.</div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {paginatedLogs.map((log) => {
              const badgeColors = {
                TICKET: { bg: 'rgba(59,130,246,0.15)', text: '#3b82f6' },
                SUBSCRIPTION: { bg: 'rgba(16,185,129,0.15)', text: '#10b981' },
                DEBT: { bg: 'rgba(217,119,6,0.15)', text: '#f59e0b' },
                GENERAL: { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' },
              }[log.type] || { bg: 'rgba(148,163,184,0.15)', text: '#94a3b8' };

              return (
                <div
                  key={log.id}
                  style={{
                    background: log.status === 'UNREAD' ? (isLight ? '#f8fafc' : 'rgba(59,130,246,0.05)') : 'transparent',
                    border: `1px solid ${log.status === 'UNREAD' ? 'rgba(59,130,246,0.25)' : (isLight ? '#f1f5f9' : 'rgba(255,255,255,0.05)')}`,
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    transition: 'all 0.2s',
                  }}
                >
                  <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', flex: 1 }}>
                    {/* Status dot */}
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: log.status === 'UNREAD' ? '#3b82f6' : 'transparent',
                      marginTop: '6px',
                      flexShrink: 0
                    }} />

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{
                          color: isLight ? '#0f172a' : '#e2e8f0',
                          fontWeight: log.status === 'UNREAD' ? 700 : 500,
                          fontSize: '15px'
                        }}>
                          {log.title}
                        </span>
                        <span style={{
                          background: badgeColors.bg,
                          color: badgeColors.text,
                          padding: '2px 8px',
                          borderRadius: '4px',
                          fontSize: '10.5px',
                          fontWeight: 700
                        }}>
                          {log.type === 'TICKET' ? 'تذكرة' :
                           log.type === 'SUBSCRIPTION' ? 'اشتراك' :
                           log.type === 'DEBT' ? 'دين' : 'عام'}
                        </span>
                        {log.success !== undefined && (
                          <span style={{
                            background: log.success ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)',
                            color: log.success ? '#10b981' : '#ef4444',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '10.5px',
                            fontWeight: 600
                          }}>
                            {log.success ? 'مُرسل بنجاح' : 'غير مسلّم'}
                          </span>
                        )}
                      </div>
                      <p style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '13.5px', margin: '4px 0', lineHeight: 1.5 }}>
                        {log.body}
                      </p>
                      <span style={{ color: '#64748b', fontSize: '11px' }}>
                        {new Date(log.createdAt).toLocaleString('ar-IQ')}
                      </span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', marginLeft: '12px' }}>
                    {log.status === 'UNREAD' ? (
                      <button
                        onClick={() => markAsRead(log.id)}
                        title="تحديد كمقروء"
                        style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: '1px solid rgba(255,255,255,0.1)',
                          borderRadius: '8px',
                          padding: '8px',
                          color: '#3b82f6',
                          cursor: 'pointer',
                        }}
                      >
                        <Eye size={16} />
                      </button>
                    ) : (
                      <div style={{ padding: '8px', color: '#64748b' }}>
                        <CheckCircle2 size={16} />
                      </div>
                    )}
                    <button
                      onClick={() => deleteNotification(log.id)}
                      title="حذف"
                      style={{
                        background: 'rgba(239,68,68,0.05)',
                        border: '1px solid rgba(239,68,68,0.15)',
                        borderRadius: '8px',
                        padding: '8px',
                        color: '#f87171',
                        cursor: 'pointer',
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: '20px',
                paddingTop: '20px',
                borderTop: isLight ? '1px solid #f1f5f9' : '1px solid rgba(255,255,255,0.06)',
                gap: '16px',
                flexWrap: 'wrap',
                direction: 'rtl'
              }}>
                {/* Info */}
                <div style={{ color: isLight ? '#475569' : '#64748b', fontSize: '13.5px', fontWeight: 500 }}>
                  {i18n.language === 'ar' ? (
                    `عرض ${(pageNumber - 1) * pageSize + 1}-${Math.min(pageNumber * pageSize, totalLogsCount)} من أصل ${totalLogsCount} إشعار`
                  ) : (
                    `Showing ${(pageNumber - 1) * pageSize + 1}-${Math.min(pageNumber * pageSize, totalLogsCount)} of ${totalLogsCount} notifications`
                  )}
                </div>

                {/* Page Navigation */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <button
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                    className="pagination-btn"
                  >
                    <ChevronRight size={16} />
                    {i18n.language === 'ar' ? 'السابق' : 'Prev'}
                  </button>
                  
                  <span style={{ color: isLight ? '#475569' : '#e2e8f0', fontSize: '13.5px', fontWeight: 600 }}>
                    {i18n.language === 'ar' ? `الصفحة ${pageNumber} من ${totalPages}` : `Page ${pageNumber} of ${totalPages}`}
                  </span>

                  <button
                    onClick={() => setPageNumber((prev) => Math.min(prev + 1, totalPages))}
                    disabled={pageNumber >= totalPages}
                    className="pagination-btn"
                  >
                    {i18n.language === 'ar' ? 'التالي' : 'Next'}
                    <ChevronLeft size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationCenterPage;
